import { useContext } from 'react';
import { useTheme } from '@mui/material/styles';
import Box from '@mui/material/Box';
import Drawer from '@mui/material/Drawer';
import CssBaseline from '@mui/material/CssBaseline';
import Toolbar from '@mui/material/Toolbar';
import List from '@mui/material/List';
import Typography from '@mui/material/Typography';
import Divider from '@mui/material/Divider';
import IconButton from '@mui/material/IconButton';
import MenuIcon from '@mui/icons-material/Menu';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import ListItem from '@mui/material/ListItem';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import AppBar from './AppBar';
import DrawerHeader from './DrawerHeader';

import { Avatar } from '@mui/material';
import LogoutIcon from '@mui/icons-material/Logout';
import TuneIcon from '@mui/icons-material/Tune';
import ManageSearchIcon from '@mui/icons-material/ManageSearch';
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import NotesIcon from '@mui/icons-material/Notes';
import GroupIcon from '@mui/icons-material/Group';
import LibraryAddIcon from '@mui/icons-material/LibraryAdd';

import { AuthContext } from '../utils/AuthContext';

import { Link, useLocation, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import global from '../utils/Global'
import FolderIcon from '@mui/icons-material/Folder';
import PercentIcon from '@mui/icons-material/Percent';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import Global from '../utils/Global';
import { clearAllPageState } from '../utils/pageState';
import { clearSharedClients } from '../utils/sharedClients';



const drawerWidth = 240;
const activeColor = '#e3f2fd';

export default function MainHeader({ open, setOpen, hasUnsavedData = false, color, blockNavigation = false, blockNavigationMessage = 'Please wait for the current request to finish.' }) {
    const { auth, setAuth } = useContext(AuthContext);
    const theme = useTheme();

    const navigate = useNavigate()
    const location = useLocation()

    // A route with an id param (/auto_quotation/:id) is still the same page as far as the
    // menu is concerned, so sub-paths count as active too.
    const isActive = (path) => path && (location.pathname === path || location.pathname.startsWith(path + '/'))

    // The item pointing at the page we are already on gets highlighted, and keeps that
    // highlight on hover so it does not look like a plain hover state.
    const itemStyle = (path) => ({
        minHeight: 48,
        justifyContent: open ? 'initial' : 'center',
        px: 2.5,
        ...(isActive(path) && {
            backgroundColor: activeColor,
            '&:hover': { backgroundColor: activeColor },
        }),
    })

    const handleDrawerOpen = () => {
        setOpen(true);

        localStorage.setItem('quoting-drawer-open', true);
    };

    const handleDrawerClose = () => {
        setOpen(false);

        localStorage.setItem('quoting-drawer-open', false);
    };

    const goToQuotation = () => {
        if (!canLeavePage()) {
            return;
        }

        navigate('/quotation')
    }

    const goToQuotationList = () => {
        if (!canLeavePage()) {
            return;
        }

        navigate('/quotation_list')
    }

    const goToMyTobPlans = () => {
        if (!canLeavePage()) {
            return;
        }

        navigate('/my_tob_plans')
    }

    const goToUsers = () => {
        if (!canLeavePage()) {
            return;
        }

        navigate('/users')
    }

    const goToInsurerRates = () => {
        if (!canLeavePage()) {
            return;
        }

        navigate('/insurer_rates')
    }

    const goToAppFiles = () => {
        if (!canLeavePage()) {
            return;
        }

        navigate('/app_files')
    }

    const goToPremiumIncreaseCalculator = () => {
        if (!canLeavePage()) {
            return;
        }

        navigate('/premium_increase_calculator')
    }

    const goToAutoQuotation = () => {
        if (!canLeavePage()) {
            return;
        }

        navigate('/auto_quotation')
    }

    const goToSetting = () => {
        if (!canLeavePage()) {
            return;
        }

        navigate('/change_password')
    }

    const handleLogout = () => {
        

        localStorage.removeItem('quoting-api-token');
        localStorage.removeItem('quoting-current-user');

        // The saved page state and clients belong to the session that is ending, so they
        // must not be waiting for whoever logs in next on this device.
        clearAllPageState();
        clearSharedClients();

        setAuth({
            token: null,
            user: null
        });

        navigate('/login');
    }

    const handleClickLogout = () => {
        if (!canLeavePage()) {
            return;
        }

        handleLogout();
    }

    function canLeavePage() {
        // The page owning this header can have work in flight (a fetch it will write into
        // its own state), so leaving mid-request would drop that result on an unmounted page.
        if (blockNavigation) {
            toast.info(blockNavigationMessage, { hideProgressBar: true });
            return false;
        }

        // if (hasUnsavedData) {
        //     return window.confirm("You have unsaved quotation data. Do you want to leave without saving?");
        // }
        return true;
    }



    return (
        <>
            <CssBaseline />
            <AppBar position="fixed" open={open} sx={color ? { backgroundColor: color } : {}}>
                <Toolbar>
                    <IconButton
                        color="inherit"
                        aria-label="open drawer"
                        onClick={handleDrawerOpen}
                        edge="start"
                        sx={{ mr: 2, ...(open && { display: 'none' }) }}
                    >
                        <MenuIcon />
                    </IconButton>
                    <Typography variant="h6" noWrap component="div">
                        Medishure Quote Tool
                    </Typography>
                </Toolbar>
            </AppBar>
            <Drawer
                sx={{
                    width: drawerWidth,
                    flexShrink: 0,
                    '& .MuiDrawer-paper': {
                        width: drawerWidth,
                        boxSizing: 'border-box',
                    },
                }}
                variant="persistent"
                anchor="left"
                open={open}
            >
                <DrawerHeader>
                    <IconButton onClick={handleDrawerClose}>
                        {theme.direction === 'ltr' ? <ChevronLeftIcon /> : <ChevronRightIcon />}
                    </IconButton>
                </DrawerHeader>

                <Box sx={{ mb: '13px', width: '100%', display: 'flex', alignItems: 'center', flexDirection: 'column' }}>
                    <Avatar sx={{ mb: '10px', width: 80, height: 80 }} alt={auth.user.given_name + " " + auth.user.last_name} src={global.storageURL + '/storage/uploads/' + auth.user.image} />
                    {
                        open ?
                            <Box sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center' }}>
                                <Typography variant='h6' component="div" sx={{ textAlign: 'center' }}>
                                    {auth.user.given_name + " " + auth.user.last_name}
                                </Typography>
                                <Link to="/profile" style={{ textDecoration: 'none' }} onClick={(e) => { if (!canLeavePage()) e.preventDefault(); }}>Edit</Link>
                            </Box>
                            : ''
                    }

                </Box>
                <Divider />

                <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', justifyContent: 'space-between' }}>

                    <List>
                        <ListItem onClick={goToQuotation} key={'Quotation'} disablePadding sx={{ display: 'block' }}>
                            <ListItemButton sx={itemStyle('/quotation')}>
                                <ListItemIcon
                                    sx={{
                                        minWidth: 0,
                                        mr: open ? 3 : 'auto',
                                        justifyContent: 'center',
                                    }}
                                >
                                    <ManageSearchIcon />

                                </ListItemIcon>
                                <ListItemText primary='Calculators' sx={{ opacity: open ? 1 : 0 }} />
                            </ListItemButton>
                        </ListItem>
                        
                        <ListItem onClick={goToAutoQuotation} key={'Auto Quotation'} disablePadding sx={{ display: 'block' }}>
                            <ListItemButton sx={itemStyle('/auto_quotation')}>
                                <ListItemIcon
                                    sx={{
                                        minWidth: 0,
                                        mr: open ? 3 : 'auto',
                                        justifyContent: 'center',
                                    }}
                                >
                                    <AutoAwesomeIcon />

                                </ListItemIcon>
                                <ListItemText primary='Auto Quotation' sx={{ opacity: open ? 1 : 0 }} />
                            </ListItemButton>
                        </ListItem>

                        <ListItem onClick={goToQuotationList} key={'Saved Quotations'} disablePadding sx={{ display: 'block' }}>
                            <ListItemButton sx={itemStyle('/quotation_list')}>
                                <ListItemIcon
                                    sx={{
                                        minWidth: 0,
                                        mr: open ? 3 : 'auto',
                                        justifyContent: 'center',
                                    }}
                                >
                                    <NotesIcon />

                                </ListItemIcon>
                                <ListItemText primary='Saved Quotations' sx={{ opacity: open ? 1 : 0 }} />
                            </ListItemButton>
                        </ListItem>

                        <ListItem onClick={goToMyTobPlans} key={'My TOB Plans'} disablePadding sx={{ display: 'block' }}>
                            <ListItemButton sx={itemStyle('/my_tob_plans')}>
                                <ListItemIcon
                                    sx={{
                                        minWidth: 0,
                                        mr: open ? 3 : 'auto',
                                        justifyContent: 'center',
                                    }}
                                >
                                    <LibraryAddIcon />

                                </ListItemIcon>
                                <ListItemText primary='My TOB Plans' sx={{ opacity: open ? 1 : 0 }} />
                            </ListItemButton>
                        </ListItem>
                        
                        <ListItem onClick={goToPremiumIncreaseCalculator} key={'Premium Increase Calculator'} disablePadding sx={{ display: 'block' }}>
                            <ListItemButton sx={itemStyle('/premium_increase_calculator')}>
                                <ListItemIcon
                                    sx={{
                                        minWidth: 0,
                                        mr: open ? 3 : 'auto',
                                        justifyContent: 'center',
                                    }}
                                >
                                    <PercentIcon />

                                </ListItemIcon>
                                <ListItemText primary='Premium Increase Calculator' sx={{ opacity: open ? 1 : 0 }} />
                            </ListItemButton>
                        </ListItem>
                        

                        {
                            (auth.user.role_id === Global.ROLE_SUPER_ADMIN || auth.user.role_id === Global.ROLE_ADMIN) &&
                            <ListItem onClick={goToUsers} key={'Users'} disablePadding sx={{ display: 'block' }}>
                                <ListItemButton sx={itemStyle('/users')}>
                                    <ListItemIcon
                                        sx={{
                                            minWidth: 0,
                                            mr: open ? 3 : 'auto',
                                            justifyContent: 'center',
                                        }}
                                    >
                                        <GroupIcon />

                                    </ListItemIcon>
                                    <ListItemText primary='Users' sx={{ opacity: open ? 1 : 0 }} />
                                </ListItemButton>
                            </ListItem>
                        }

                        {
                            (auth.user.role_id === Global.ROLE_SUPER_ADMIN || auth.user.role_id === Global.ROLE_ADMIN) &&
                            <ListItem onClick={goToInsurerRates} key={'Insurer Rates'} disablePadding sx={{ display: 'block' }}>
                                <ListItemButton sx={itemStyle('/insurer_rates')}>
                                    <ListItemIcon
                                        sx={{
                                            minWidth: 0,
                                            mr: open ? 3 : 'auto',
                                            justifyContent: 'center',
                                        }}
                                    >
                                        <TrendingUpIcon />

                                    </ListItemIcon>
                                    <ListItemText primary='Insurer Rates' sx={{ opacity: open ? 1 : 0 }} />
                                </ListItemButton>
                            </ListItem>
                        }

                        {
                            (auth.user.role_id === Global.ROLE_SUPER_ADMIN || auth.user.role_id === Global.ROLE_ADMIN) &&
                            <ListItem onClick={goToAppFiles} key={'App Files'} disablePadding sx={{ display: 'block' }}>
                                <ListItemButton sx={itemStyle('/app_files')}>
                                    <ListItemIcon
                                        sx={{
                                            minWidth: 0,
                                            mr: open ? 3 : 'auto',
                                            justifyContent: 'center',
                                        }}
                                    >
                                        <FolderIcon />

                                    </ListItemIcon>
                                    <ListItemText primary='App Files' sx={{ opacity: open ? 1 : 0 }} />
                                </ListItemButton>
                            </ListItem>
                        }
                    </List>

                    <List>
                        <ListItem onClick={handleClickLogout} key={'Logout'} disablePadding sx={{ display: 'block' }}>
                            <ListItemButton sx={itemStyle()}>
                                <ListItemIcon
                                    sx={{
                                        minWidth: 0,
                                        mr: open ? 3 : 'auto',
                                        justifyContent: 'center',
                                    }}
                                >
                                    <LogoutIcon />

                                </ListItemIcon>
                                <ListItemText primary='Logout' sx={{ opacity: open ? 1 : 0 }} />
                            </ListItemButton>
                        </ListItem>
                        <ListItem onClick={goToSetting} key={'Setting'} disablePadding sx={{ display: 'block' }}>
                            <ListItemButton sx={itemStyle('/change_password')}>
                                <ListItemIcon
                                    sx={{
                                        minWidth: 0,
                                        mr: open ? 3 : 'auto',
                                        justifyContent: 'center',
                                    }}
                                >
                                    <TuneIcon />

                                </ListItemIcon>
                                <ListItemText primary='Setting' sx={{ opacity: open ? 1 : 0 }} />
                            </ListItemButton>
                        </ListItem>
                    </List>
                </Box>

                <Box sx={{ height: '20px' }}>
                </Box>
            </Drawer>
        </>
    )
}
